/* ==============================================================================================
   Find Your Size (templates/page.find-your-size.liquid)

   WHY THIS IS AN ASSET AND NOT AN INLINE SCRIPT
   ---------------------------------------------
   It used to be an inline <script> at the bottom of the page template, and on the live theme it
   NEVER EXECUTED - the tag sat in the DOM and the page stayed on "Loading watch models..." for
   good. The script itself was fine (it parses, and pasting it into the console makes the page work
   perfectly), and other inline scripts on the same page - the watch selector's - run normally. The
   difference is that this one lives inside <main>, a page TEMPLATE subtree, and something on the
   page re-injects that subtree with innerHTML after load. innerHTML never executes scripts, inline
   OR external, so the tag is inserted already-dead.

   That is why this file does not just move the code out and stop there:

     - Every listener is DELEGATED off `document`, so it keeps working when the controls it points
       at are thrown away and rebuilt by whatever re-renders <main>.
     - A MutationObserver re-renders the table whenever #sfContainer reappears, so a subtree that
       is replaced after we have already drawn does not go back to being blank.
     - The catalogue fetch is memoised in a promise, so any number of re-renders costs one request.
     - init() is idempotent and safe to call before the markup exists.

   Loaded from the template with `defer`. If the injection turns out to also swallow that tag, the
   include can move to layout/theme.liquid gated on the page handle without changing this file -
   nothing in here assumes when it runs.
   ============================================================================================== */
(function () {
  'use strict';

  var CATALOGUE_URL = 'https://crm.strapify.com.au/watch-catalog.json';

  /* A query is only tested against model/part numbers from this length up. Shoppers type case
     sizes ("42", "38"), and those two-character strings also occur inside A-numbers such as A3452
     and A1858 - matching them there would bury the real 42mm rows. "A3324" and "MEHX4X/A" are well
     clear of it, and so is a partial like "3324". Keep in step with NUMBER_SEARCH_MIN_LENGTH in
     sections/watch-selector-final.liquid and SMM_NUMBER_MIN in sections/megamenu.liquid. */
  var NUMBER_SEARCH_MIN_LENGTH = 4;

  var globalData = [];
  var currentBrand = 'All';
  var currentQuery = '';
  var cataloguePromise = null;
  var loadFailed = false;
  var brandsRenderedFor = null;   // the <select> node we last populated
  var renderedContainer = null;   // the #sfContainer node we last drew into (see watchForMarkup)
  var debounceTimer = null;

  /* ---------------------------------------------------------------- element helpers ---------- */
  function $(id) { return document.getElementById(id); }
  function present() { return !!($('sfContainer') && $('sfSearch') && $('sfBrandFilter')); }

  /* ---------------------------------------------------------------- data -------------------- */
  /* The CRM feed first, and when it does not answer - down, or slower than FEED_TIMEOUT_MS - the
     copy the CRM keeps on Shopify's CDN (window.STRAPIFY_FEED_FALLBACK, set in theme.liquid). On
     17 Sep 2026 the CRM was down for 2h40m and this list was empty for every shopper the whole
     time. The CDN url gets a 15-minute cache bucket of our own: Shopify's CDN caches every url
     variant for a year, and any key it has not seen is a miss to origin, so the copy is never
     more than 15 minutes older than the CRM's last push. A fallback that fails too just leaves
     the list empty, exactly as before. */
  var FEED_TIMEOUT_MS = 4000;

  function fetchWithTimeout(url, ms) {
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, ms) : null;
    return fetch(url, ctrl ? { signal: ctrl.signal } : {})
      .then(function (r) { if (timer) clearTimeout(timer); if (!r.ok) throw new Error('HTTP ' + r.status); return r; },
            function (e) { if (timer) clearTimeout(timer); throw e; });
  }

  function fetchCatalogueWithFallback() {
    return fetchWithTimeout(CATALOGUE_URL, FEED_TIMEOUT_MS).catch(function (err) {
      var fb = window.STRAPIFY_FEED_FALLBACK;
      if (!fb || !fb.catalog) throw err;
      var bucket = Math.floor(Date.now() / (fb.bucketMs || 900000));
      try { console.warn('watch catalogue: CRM unavailable, using CDN copy', err && err.message); } catch (e) {}
      return fetchWithTimeout(fb.catalog + '?t=' + bucket, FEED_TIMEOUT_MS * 2);
    });
  }

  function loadCatalogue() {
    if (cataloguePromise) return cataloguePromise;
    cataloguePromise = fetchCatalogueWithFallback()
      .then(function (r) { return r.json(); })
      .then(function (rows) {
        globalData = (rows || []).map(function (r) {
          return {
            brand: (r.brand || '').trim(),
            model: (r.model || '').trim(),
            sizes: (r.sizes || '').trim(),
            url: (r.url || '').trim(),
            // Manufacturer identifiers, currently Apple only and blank on every other row: the
            // A-number engraved on the case back, and the longer order code the watch shows under
            // Settings > General > About.
            modelNumbers: (r.model_numbers || '').trim(),
            partNumbers: (r.part_numbers || '').trim()
          };
        });
        return globalData;
      })
      .catch(function () {
        loadFailed = true;
        globalData = [];
        return globalData;
      });
    return cataloguePromise;
  }

  /* ---------------------------------------------------------------- URL state --------------- */
  function readStateFromUrl() {
    var sel = $('sfBrandFilter');
    var brandFromHash = '';
    try { brandFromHash = decodeURIComponent((window.location.hash || '').replace('#', '')); } catch (e) {}
    var queryFromUrl = '';
    try { queryFromUrl = new URL(window.location).searchParams.get('q') || ''; } catch (e) {}

    if (brandFromHash && sel) {
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value.toLowerCase() === brandFromHash.toLowerCase()) {
          currentBrand = sel.options[i].value;
          sel.value = currentBrand;
          break;
        }
      }
    }
    if (queryFromUrl) {
      currentQuery = queryFromUrl;
      if ($('sfSearch')) $('sfSearch').value = queryFromUrl;
    }
  }

  function syncUrl() {
    try {
      var url = new URL(window.location);
      if (currentQuery) url.searchParams.set('q', currentQuery);
      else url.searchParams.delete('q');
      window.history.replaceState({}, '', url);
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- rendering --------------- */
  function populateBrands() {
    var sel = $('sfBrandFilter');
    if (!sel || brandsRenderedFor === sel) return;
    var brands = [];
    var seen = {};
    globalData.forEach(function (d) {
      var b = (d.brand || '').trim();
      if (b && !seen[b]) { seen[b] = 1; brands.push(b); }
    });
    brands.sort();
    // Rebuild from scratch: after a re-render the select is a fresh node with only "All Brands".
    sel.innerHTML = '<option value="All">All Brands</option>';
    brands.forEach(function (b) {
      var o = document.createElement('option');
      o.value = o.textContent = b;
      sel.appendChild(o);
    });
    sel.value = currentBrand;
    brandsRenderedFor = sel;
  }

  // Two ways to identify a number, because one rule cannot serve both halves of the problem:
  // a 2+ char PREFIX containing a digit ("a1" -> A1553), so typing a model number from the front
  // gives results as you go rather than a dead zone until the 4th character; or 4+ characters
  // appearing anywhere ("3324"), for pasting a fragment.
  //
  // ⚠ The digit requirement is what makes the short prefix safe: without it "mk" would prefix
  // -match MKN03LL/A and pull Apple rows into a Michael Kors search. And nothing starts with
  // "42"/"38", so a bare case size still cannot drag in unrelated rows.
  // Keep in step with sections/watch-selector-final.liquid and sections/megamenu.liquid.
  var NUMBER_PREFIX_MIN_LENGTH = 2;

  function numberListFor(row) {
    return ((row.modelNumbers || '') + ',' + (row.partNumbers || ''))
      .split(',').map(function (n) { return n.trim().toLowerCase(); }).filter(Boolean);
  }

  function matchesNumber(row, q) {
    if (!q) return false;
    if (q.length >= NUMBER_SEARCH_MIN_LENGTH
        && ((row.modelNumbers || '') + ' ' + (row.partNumbers || '')).toLowerCase().indexOf(q) >= 0) {
      return true;
    }
    if (q.length >= NUMBER_PREFIX_MIN_LENGTH && /\d/.test(q)) {
      return numberListFor(row).some(function (n) { return n.indexOf(q) === 0; });
    }
    return false;
  }

  function highlight(text, query) {
    if (!query) return text;
    var out = text;
    query.trim().split(/\s+/).filter(Boolean).forEach(function (w) {
      var esc = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp('(' + esc + ')', 'ig'), '<mark>$1</mark>');
    });
    return out;
  }

  function buildTable(data, query) {
    var html = '<table class="sf-table"><thead><tr>' +
               '<th>Brand</th><th>Model</th><th>Strap Size</th>' +
               '</tr></thead><tbody>';
    data.forEach(function (d) {
      var brand = d.brand || '';
      var model = d.model || '';
      var sizes = d.sizes || '';
      var url = d.url || '';
      var sizeHL = highlight(sizes, query);
      var sizeCell = (url && !/We(.+?)anything/.test(sizes))
        ? '<a href="' + url + '" target="_blank" rel="noopener">' + sizeHL + ' <span class="sf-arrow">↗</span></a>'
        : sizeHL;
      // Shown so a search for "A3324" visibly explains itself, and so anyone reading the table can
      // check their own watch against it. Blank on every row without numbers (i.e. all non-Apple).
      var numbers = (d.modelNumbers || '').trim();
      var modelCell = highlight(model, query) +
        (numbers ? '<div class="sf-modelno">Model ' + highlight(numbers, query) + '</div>' : '');
      html += '<tr><td>' + highlight(brand, query) + '</td>' +
              '<td>' + modelCell + '</td>' +
              '<td>' + sizeCell + '</td></tr>';
    });
    return html + '</tbody></table>';
  }

  function applyFilters() {
    var container = $('sfContainer');
    var countEl = $('sfResultCount');
    if (!container) return;

    if (loadFailed) {
      container.innerHTML = '<div class="sf-state"><strong>Something went wrong</strong>' +
                            'Please refresh the page and try again.</div>';
      if (countEl) countEl.textContent = '';
      renderedContainer = container;
      return;
    }
    if (!globalData.length) {
      container.innerHTML = '<div class="sf-state"><span>Loading watch models…</span></div>';
      renderedContainer = container;
      return;
    }

    var byBrand = currentBrand === 'All'
      ? globalData
      : globalData.filter(function (d) { return d.brand === currentBrand; });

    var q = (currentQuery || '').trim().toLowerCase();
    var filtered = !q ? byBrand : byBrand.filter(function (d) {
      return ((d.brand || '') + ' ' + (d.model || '') + ' ' + (d.sizes || '')).toLowerCase().indexOf(q) >= 0
        || matchesNumber(d, q);
    });

    syncUrl();

    if (!filtered.length) {
      if (countEl) countEl.textContent = '';
      container.innerHTML = '<div class="sf-state"><strong>No results found</strong>' +
                            'Try a different brand or model name.</div>';
      renderedContainer = container;
      return;
    }
    if (countEl) {
      countEl.textContent = filtered.length + ' model' + (filtered.length !== 1 ? 's' : '') + ' found';
    }
    container.innerHTML = buildTable(filtered, currentQuery);
    renderedContainer = container;
  }

  function render() {
    populateBrands();
    applyFilters();
  }

  /* ---------------------------------------------------------------- delegated events --------
     Bound to `document`, not to the controls, so they survive <main> being replaced. */
  document.addEventListener('input', function (e) {
    if (!e.target || e.target.id !== 'sfSearch') return;
    currentQuery = e.target.value;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyFilters, 120);
  });

  document.addEventListener('change', function (e) {
    if (!e.target || e.target.id !== 'sfBrandFilter') return;
    currentBrand = e.target.value;
    try { window.location.hash = encodeURIComponent(currentBrand); } catch (err) {}
    applyFilters();
  });

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#sfResetBtn') : null;
    if (!btn) return;
    currentBrand = 'All';
    currentQuery = '';
    if ($('sfBrandFilter')) $('sfBrandFilter').value = 'All';
    if ($('sfSearch')) $('sfSearch').value = '';
    try {
      var url = new URL(window.location);
      url.searchParams.delete('q');
      window.history.replaceState({}, '', url);
      window.location.hash = '';
    } catch (err) {}
    applyFilters();
    if ($('sfSearch')) $('sfSearch').focus();
  });

  /* ---------------------------------------------------------------- boot -------------------- */
  var initialised = false;

  function init() {
    if (!present()) return;
    if (!initialised) {
      initialised = true;
      loadCatalogue().then(function () {
        readStateFromUrl();
        render();
      });
      return;
    }
    // Already initialised and the markup has come back (re-render): redraw into the new nodes.
    brandsRenderedFor = null;
    if ($('sfSearch') && currentQuery) $('sfSearch').value = currentQuery;
    render();
  }

  function watchForMarkup() {
    if (!document.body) return;
    var observer = new MutationObserver(function () {
      var container = $('sfContainer');
      var sel = $('sfBrandFilter');

      // Node IDENTITY, not content: our own innerHTML writes also fire this observer, and a
      // legitimate "No results found" render contains no .sf-table - testing content would
      // re-render forever. A container we have not drawn into is a genuinely new subtree.
      if (container && container !== renderedContainer) { init(); return; }

      // The controls get swapped INDEPENDENTLY of the table - observed live: the table rendered
      // with 1966 rows while the brand <select> had been replaced by a fresh one holding only
      // "All Brands". So the container check alone is not enough to keep the page consistent.
      // Terminates: filling the select makes options.length > 1, so the next pass is a no-op.
      if (sel && globalData.length && sel.options.length <= 1) {
        brandsRenderedFor = null;
        populateBrands();
      }

      // Same for the search box losing the query it should be showing.
      var input = $('sfSearch');
      if (input && currentQuery && input.value !== currentQuery) input.value = currentQuery;
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); watchForMarkup(); });
  } else {
    init();
    watchForMarkup();
  }
})();
